import { EditingMode, PermissionRole } from '@aline/common';
import { useEditorTabStateSelector } from 'src/editor/editor-tab-state-registry';
import { useEditorLoading } from 'src/layout-engine';

interface UsePermissionRole {
  canComment: boolean;
  canEdit: boolean;
  canSuggest: boolean;
  canView: boolean;
  permissionRole: PermissionRole | null;
}

// Hook used to determine whether editing controls should be disabled.
export function useEditingDisabled(): boolean {
  const editingMode = useCurrentEditingMode();
  const editorLoading = useEditorLoading();

  return (
    editorLoading || (editingMode !== EditingMode.EDITING && editingMode !== EditingMode.SUGGESTING)
  );
}

export function useCurrentUserPermissionRole(): UsePermissionRole {
  const permissionRole = useEditorTabStateSelector(state => state.documentUi.permissionRole);
  const canEdit =
    permissionRole === PermissionRole.Editor || permissionRole === PermissionRole.Owner;
  const canSuggest = canEdit || permissionRole === PermissionRole.Suggester;
  const canComment = canSuggest || permissionRole === PermissionRole.Commenter;

  return {
    canComment,
    canEdit,
    canSuggest,
    canView: canComment || permissionRole === PermissionRole.Viewer,
    permissionRole,
  };
}

interface Mode {
  commenting: boolean;
  editing: boolean;
  editingMode: EditingMode | null;
  suggesting: boolean;
  viewing: boolean;
}

export function useEditingMode(): Mode {
  const editingMode = useEditorTabStateSelector(state => state.documentUi.editingMode);

  return {
    commenting: editingMode === EditingMode.COMMENTING,
    editing: editingMode === EditingMode.EDITING,
    editingMode,
    suggesting: editingMode === EditingMode.SUGGESTING,
    viewing: editingMode === EditingMode.VIEWING,
  };
}

export function useCurrentEditingMode(): EditingMode | null {
  const { editingMode } = useEditingMode();
  return editingMode;
}
